export const HEADING = "var(--logoColor2)";

export const BODY = "var(--logoColor)";

export const GOLD = "var(--logoColor2)";

export const GROUND = "var(--backgroundColor)";

export const ON_GOLD = "var(--color1)";

export const SURFACE = "color-mix(in srgb, var(--color5) 62%, transparent)";

export const SURFACE_STRONG = "var(--color5)";

export const HAIRLINE_COLOR = "color-mix(in srgb, var(--logoColor) 26%, transparent)";

export const HAIRLINE = `1px solid ${HAIRLINE_COLOR}`;

export const HAIRLINE_SOFT = "1px solid color-mix(in srgb, var(--logoColor) 16%, transparent)";

export const WASH = "color-mix(in srgb, var(--logoColor) 8%, transparent)";

export const DISABLED_INK = "color-mix(in srgb, var(--logoColor) 38%, transparent)";

export const ERROR_INK = "#e0806f";

export const EASE = "cubic-bezier(0.23, 1, 0.32, 1)";

/**
 * Opaque panel behind a floating menu. `SURFACE` is translucent, which is fine
 * over a section but not over the grid a dropdown covers.
 */
export const MENU_SURFACE = "var(--color1, #101010)";

/**
 * Layer for a `Select` popup. `ArtistNav` portals into the body and full screen
 * overlays start above 9999 (BaseModal 10020, artist gate 10030, the store's own
 * drawers 10010), so a dropdown has to clear every one of them: it is always the
 * thing the visitor is touching, including inside a drawer.
 */
export const SELECT_POPUP_Z = 10050;

/** Drawers in the store (bag, filters) share one layer. */
export const DRAWER_Z = 10010;

/** Above this width the store's filters move into a sticky left column. */
export const FILTER_SIDEBAR_MIN_WIDTH = 1360;

/** Below this width the filters collapse into a bottom drawer. */
export const FILTER_DRAWER_MAX_WIDTH = 899.95;

export const DISPLAY_SIZE = { xs: "42px", md: "clamp(30px, 2.1rem, 40px)" };

export const LEDE_SIZE = { xs: "18px", md: "clamp(15px, 0.95rem, 18px)" };

export const SECTION_SIZE = { xs: "30px", md: "clamp(24px, 1.55rem, 29px)" };

export const BODY_SIZE = { xs: "17px", md: "clamp(14px, 0.85rem, 16px)" };

export const SECTION_GAP = { xs: "56px", md: "80px" };

export const SAFE_BOTTOM = "env(safe-area-inset-bottom, 0px)";

export const STICKY_BAR_HEIGHT = 56;

export const STICKY_BAR_INSET = 12;

export const BAG_BUTTON_BOTTOM = {
  xs: `calc(62px + 0.5rem + 10px + 58px + 12px + ${SAFE_BOTTOM})`,
  md: "32px",
};

export const displaySx = {
  fontFamily: "Hanson, sans-serif",
  fontWeight: 700,
  fontSize: DISPLAY_SIZE,
  lineHeight: 1.05,
  letterSpacing: "-0.01em",
  color: HEADING,
  textWrap: "balance" as const,
  m: 0,
};

export const sectionHeadingSx = {
  fontFamily: "Hanson, sans-serif",
  fontWeight: 700,
  fontSize: SECTION_SIZE,
  lineHeight: 1.15,
  color: HEADING,
  m: 0,
};

export const ledeSx = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
  fontSize: LEDE_SIZE,
  lineHeight: 1.55,
  color: BODY,
};

export const bodySx = {
  fontFamily: "Poppins, sans-serif",
  fontWeight: 400,
  fontSize: BODY_SIZE,
  lineHeight: 1.7,
  color: BODY,
};

export const metaSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: { xs: "14px", md: "13px" },
  lineHeight: 1.6,
  color: BODY,
};

export const smallMetaSx = {
  ...metaSx,
  fontSize: { xs: "13px", md: "12px" },
};

export const eyebrowSx = {
  fontFamily: "Poppins, sans-serif",
  fontSize: { xs: "12px", md: "11px" },
  fontWeight: 600,
  letterSpacing: "0.16em",
  textTransform: "uppercase" as const,
  color: BODY,
  opacity: 0.7,
};

export const pillSx = {
  minHeight: 48,
  px: "26px",
  borderRadius: "999px",
  fontFamily: "Poppins, sans-serif",
  fontSize: { xs: "15px", md: "14px" },
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  backgroundColor: GOLD,
  color: ON_GOLD,
  transition: `background-color 180ms ${EASE}, transform 180ms ${EASE}`,
  "&:hover": { backgroundColor: BODY },
  "&:active": { transform: "scale(0.985)" },
  "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "3px" },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
};

export const selectorSx = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "10px",
  minHeight: { xs: 52, md: 48 },
  px: { xs: "18px", md: "16px" },
  py: 0,
  cursor: "pointer",
  borderRadius: "10px",
  fontFamily: "Poppins, sans-serif",
  fontSize: { xs: "16px", md: "14px" },
  fontWeight: 600,
  letterSpacing: "0.03em",
  lineHeight: 1.2,
  whiteSpace: "nowrap" as const,
  color: BODY,
  backgroundColor: "transparent",
  border: HAIRLINE,
  transition: `background-color 160ms ${EASE}, border-color 160ms ${EASE}, transform 160ms ${EASE}, opacity 160ms ${EASE}`,
  "&:hover": { borderColor: GOLD, backgroundColor: WASH },
  "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "2px" },
  "&:active": { transform: "scale(0.97)" },
  '&[aria-pressed="true"]': { color: ON_GOLD, backgroundColor: GOLD, borderColor: GOLD },
  '&[aria-pressed="true"]:hover': { backgroundColor: GOLD },
  '&[aria-pressed="true"] .merch-selector__meta': { color: ON_GOLD },
  "&:disabled": { cursor: "not-allowed", color: DISABLED_INK, borderColor: DISABLED_INK, backgroundColor: "transparent", transform: "none" },
  '&[data-state="loading"]': { cursor: "progress", opacity: 0.6 },
  '&[data-state="error"]': { borderColor: ERROR_INK, color: ERROR_INK },
  '&[data-state="success"]': { borderColor: GOLD },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:active": { transform: "none" } },
};

export const selectorMetaSx = {
  ...metaSx,
  fontSize: { xs: "14px", md: "13px" },
  fontWeight: 500,
  lineHeight: 1,
  opacity: 0.7,
  fontVariantNumeric: "tabular-nums" as const,
};

export const swatchSx = {
  width: { xs: 48, md: 44 },
  height: { xs: 48, md: 44 },
  p: 0,
  cursor: "pointer",
  borderRadius: "50%",
  overflow: "hidden",
  border: HAIRLINE,
  outline: "2px solid transparent",
  outlineOffset: "3px",
  transition: `transform 160ms ${EASE}, outline-color 160ms ${EASE}, opacity 160ms ${EASE}`,
  "&:hover": { transform: "scale(1.06)", outlineColor: HAIRLINE_COLOR },
  "&:active": { transform: "scale(0.94)" },
  "&:focus-visible": { outline: `2px solid ${BODY}`, outlineOffset: "3px" },
  '&[aria-pressed="true"]': { outlineColor: GOLD },
  "&:disabled": { cursor: "not-allowed", opacity: 0.35, transform: "none" },
  '&[data-state="loading"]': { cursor: "progress", opacity: 0.6 },
  '&[data-state="error"]': { outlineColor: ERROR_INK },
  "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" }, "&:active": { transform: "none" } },
};

export const ghostPillSx = {
  display: "inline-block",
  minHeight: 48,
  px: "26px",
  py: "13px",
  borderRadius: "999px",
  border: `1px solid ${GOLD}`,
  color: GOLD,
  textDecoration: "none",
  fontFamily: "Poppins, sans-serif",
  fontSize: { xs: "15px", md: "14px" },
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  "&:hover": { backgroundColor: GOLD, color: ON_GOLD },
  "&:focus-visible": { outline: `2px solid ${GOLD}`, outlineOffset: "3px" },
};
